import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { formatDate } from "@/date"
import {
  listSupplyItemsFn,
  listSupplyMovementsFn,
  listSupplyVendorsFn,
} from "@/supplies/rpc"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_supplies-layout/supplies/movements")({
  component: MovementsPage,
})

type MovementType = "receive" | "use" | "adjust"

interface MovementRow {
  id: number
  type: MovementType
  quantityDelta: number
  quantityAfter: number
  note: string | null
  invoiceNumber: string | null
  unitCostCents: number | null
  createdAt: number
  createdByUserId: number
  item?: {
    id: number
    name: string
    unit: string
    category?: { id: number; name: string } | null
  } | null
  vendor?: { id: number; name: string } | null
}

interface FilterState {
  type: "" | MovementType
  itemId: string
  vendorId: string
  createdAfter: string
  createdBefore: string
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—"
  return `$${(cents / 100).toFixed(2)}`
}

function formatDateTime(ms: number): string {
  const date = new Date(ms)
  const day = formatDate(date)
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date)
  return `${day} ${time}`
}

function movementTypeLabel(type: MovementType): string {
  switch (type) {
    case "receive":
      return "Receive"
    case "use":
      return "Use"
    case "adjust":
      return "Adjust"
  }
}

/** Local date input YYYY-MM-DD → start-of-day UTC ms */
function dateInputToStartMs(value: string): number | undefined {
  if (!value) return undefined
  const ms = Date.parse(`${value}T00:00:00.000Z`)
  return Number.isFinite(ms) ? ms : undefined
}

/** Local date input YYYY-MM-DD → end-of-day UTC ms */
function dateInputToEndMs(value: string): number | undefined {
  if (!value) return undefined
  const ms = Date.parse(`${value}T23:59:59.999Z`)
  return Number.isFinite(ms) ? ms : undefined
}

function selectClassName() {
  return cn(
    "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] md:text-sm dark:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:cursor-not-allowed disabled:opacity-50",
  )
}

function MovementsPage() {
  const [filters, setFilters] = useState<FilterState>({
    type: "",
    itemId: "",
    vendorId: "",
    createdAfter: "",
    createdBefore: "",
  })
  const [page, setPage] = useState(1)
  const pageSize = 50

  const itemsQuery = useQuery({
    queryKey: ["supply-items", { pageSize: 200, status: "all-for-filter" }],
    queryFn: async () => {
      const [active, inactive] = await Promise.all([
        listSupplyItemsFn({ data: { isActive: true, pageSize: 200 } }),
        listSupplyItemsFn({ data: { isActive: false, pageSize: 200 } }),
      ])
      return [...active.items, ...inactive.items].sort((a, b) =>
        a.name.localeCompare(b.name),
      )
    },
  })

  const vendorsQuery = useQuery({
    queryKey: ["supply-vendors", { includeInactive: true }],
    queryFn: () => listSupplyVendorsFn({ data: { includeInactive: true } }),
  })

  const listFilters = useMemo(() => {
    const itemId = filters.itemId ? Number(filters.itemId) : undefined
    const vendorId = filters.vendorId ? Number(filters.vendorId) : undefined
    return {
      type: filters.type || undefined,
      itemId: itemId != null && Number.isFinite(itemId) ? itemId : undefined,
      vendorId:
        vendorId != null && Number.isFinite(vendorId) ? vendorId : undefined,
      createdAfter: dateInputToStartMs(filters.createdAfter),
      createdBefore: dateInputToEndMs(filters.createdBefore),
      page,
      pageSize,
    }
  }, [filters, page])

  const movementsQuery = useQuery({
    queryKey: ["supply-movements", listFilters],
    queryFn: () => listSupplyMovementsFn({ data: listFilters }),
  })

  const movements = (movementsQuery.data?.movements ?? []) as MovementRow[]
  const total = movementsQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  function updateFilter<K extends keyof FilterState>(
    key: K,
    value: FilterState[K],
  ) {
    setPage(1)
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  function clearFilters() {
    setPage(1)
    setFilters({
      type: "",
      itemId: "",
      vendorId: "",
      createdAfter: "",
      createdBefore: "",
    })
  }

  const hasFilters =
    Boolean(filters.type) ||
    Boolean(filters.itemId) ||
    Boolean(filters.vendorId) ||
    Boolean(filters.createdAfter) ||
    Boolean(filters.createdBefore)

  const error = movementsQuery.isError
    ? getErrorMessage(movementsQuery.error)
    : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Movements</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Global ledger of receive, use, and adjust stock actions.
        </p>
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div className="grid gap-1.5">
            <Label htmlFor="movements-type">Type</Label>
            <select
              id="movements-type"
              value={filters.type}
              onChange={(event) =>
                updateFilter("type", event.target.value as FilterState["type"])
              }
              className={selectClassName()}
            >
              <option value="">All types</option>
              <option value="receive">Receive</option>
              <option value="use">Use</option>
              <option value="adjust">Adjust</option>
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="movements-item">Item</Label>
            <select
              id="movements-item"
              value={filters.itemId}
              onChange={(event) => updateFilter("itemId", event.target.value)}
              className={selectClassName()}
            >
              <option value="">All items</option>
              {(itemsQuery.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {!item.isActive ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="movements-vendor">Vendor</Label>
            <select
              id="movements-vendor"
              value={filters.vendorId}
              onChange={(event) => updateFilter("vendorId", event.target.value)}
              className={selectClassName()}
            >
              <option value="">All vendors</option>
              {(vendorsQuery.data ?? []).map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                  {!vendor.isActive ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="movements-after">From date</Label>
            <Input
              id="movements-after"
              type="date"
              value={filters.createdAfter}
              onChange={(event) =>
                updateFilter("createdAfter", event.target.value)
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="movements-before">To date</Label>
            <Input
              id="movements-before"
              type="date"
              value={filters.createdBefore}
              onChange={(event) =>
                updateFilter("createdBefore", event.target.value)
              }
            />
          </div>
        </div>
        {hasFilters ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ledger</CardTitle>
          <CardDescription>
            {movementsQuery.isLoading
              ? "Loading…"
              : `${total} movement${total === 1 ? "" : "s"}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          ) : movementsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : movements.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No movements match these filters.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="px-3 py-2 font-medium">When</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Item</th>
                      <th className="px-3 py-2 font-medium">Delta</th>
                      <th className="px-3 py-2 font-medium">After</th>
                      <th className="px-3 py-2 font-medium">Vendor</th>
                      <th className="px-3 py-2 font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {movements.map((movement) => (
                      <tr key={movement.id} className="hover:bg-muted/30">
                        <td className="whitespace-nowrap px-3 py-2 text-muted-foreground text-xs">
                          {formatDateTime(movement.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">
                            {movementTypeLabel(movement.type)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          {movement.item ? (
                            <Link
                              to="/supplies/items/$itemId"
                              params={{ itemId: String(movement.item.id) }}
                              className="font-medium text-primary underline-offset-4 hover:underline"
                            >
                              {movement.item.name}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium tabular-nums">
                          {movement.quantityDelta > 0
                            ? `+${movement.quantityDelta}`
                            : movement.quantityDelta}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground tabular-nums">
                          {movement.quantityAfter}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {movement.vendor?.name ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {movement.note ||
                            (movement.invoiceNumber
                              ? `Inv ${movement.invoiceNumber}`
                              : null) ||
                            (movement.unitCostCents != null
                              ? formatCents(movement.unitCostCents)
                              : null) ||
                            "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-muted-foreground text-sm">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
